import { HeatmapChartConfigDto, HeatmapChartDataDto, PlayerStat } from 'types/CodegenReportTypes';
import { ChartProps } from 'utilities/chartIdToComponent';
import StrikeZoneHeatmap from 'components/Charts/StrikeZoneHeatmap/StrikeZoneHeatmap';
import { useMemo } from 'react';
import { useGetUnitSystem } from 'hooks/helperHooks';
import { mileToKilometer } from 'utilities/constants';

type Props = ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto> & {
  playerType: 'Batter' | 'Pitcher';
};

const formatValueMap = new Map<PlayerStat | 'PitchLocation', (value: number | null) => string>([
  ['PitchLocation', (value: number | null) => (value != null ? value.toFixed(1) + '%' : '-')],
  ['ExitSpeed', (value: number | null) => (value != null ? value.toFixed(1) : '-')],
  ['BattingAverage', (value: number | null) => (value != null ? value.toFixed(3).replace('0.', '.') : '-')],
  ['Slugging', (value: number | null) => (value != null ? value.toFixed(3).replace('0.', '.') : '-')],
]);

const StrikeZoneHeatmapChart = ({ chartData, isLoading, chartConfig, savedChartConfig, playerType }: Props) => {
  const unitSystem = useGetUnitSystem();

  const formatValue = useMemo(
    () =>
      formatValueMap.get(chartConfig.viewDataAs === 'PitchLocation' ? 'PitchLocation' : chartConfig.playerStat) ||
      ((value: number | null) => (value != null ? value.toFixed(1) : '-')),
    [chartConfig]
  );

  const scale = useMemo(() => {
    if (chartConfig.viewDataAs === 'PitchLocation') return [0, 10];

    switch (chartConfig.playerStat) {
      case 'ExitSpeed':
        return unitSystem === 'Imperial' ? [70, 95] : [70 * mileToKilometer, 95 * mileToKilometer];
      case 'BattingAverage':
        return [0.175, 0.3];
      case 'Slugging':
        return [0.3, 0.6];
      default:
        return [0, 1];
    }
  }, [chartConfig, unitSystem]);

  const hasConfigEdits = useMemo(
    () => savedChartConfig.viewDataAs !== chartConfig.viewDataAs || savedChartConfig.playerStat !== chartConfig.playerStat,
    [savedChartConfig, chartConfig]
  );

  return (
    <StrikeZoneHeatmap
      chartData={hasConfigEdits ? undefined : chartData}
      isLoading={isLoading}
      playerType={playerType}
      formatValue={formatValue}
      scaleMin={scale[0]}
      scaleMax={scale[1]}
    />
  );
};

export const PitcherStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  return <StrikeZoneHeatmapChart {...props} playerType={'Pitcher'} />;
};

export const BatterStrikeZoneHeatmapChart = (props: ChartProps<HeatmapChartDataDto, HeatmapChartConfigDto>) => {
  return <StrikeZoneHeatmapChart {...props} playerType={'Batter'} />;
};
